import type { NumberRange } from '../../types';

enum Capacity {
  METRICATION = 'metrication',
  BINARY = 'binary',
}

const CAPACITY_UNIT: { [key in Capacity]: number } = {
  [Capacity.METRICATION]: 1000,
  [Capacity.BINARY]: 1024,
};

const CAPACITY_UNIT_MAP: { [key in Capacity]: string[] } = {
  [Capacity.METRICATION]: ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
  [Capacity.BINARY]: ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'],
};

type ConvertBytesRequest = {
  /**
   * bytes 데이터를 전달합니다.
   */
  bytes: number;
  /**
   * 1: KB or KiB
   *
   * 8: YB or YiB
   *
   * 연산 제곱근을 설정합니다.
   */
  convertLevel: NumberRange<1, 9>;
  /**
   * bytes 계산 유형을 선택할 수 있습니다.
   *
   * metrication: (미터법을 따라) 십진법으로 연산합니다.
   *
   * binary: 이진법으로 연산합니다.
   *
   * @default binary
   */
  convertType: Capacity;
  /**
   * 연산 후 표시할 소수점 자릿수를 설정할 수 있습니다.
   *
   * @default 2
   */
  decimalPoint: number;
  /**
   * 단위 표시 유형을 선택할 수 있습니다.
   *
   * metrication: KB, MB, GB, ...
   *
   * binary: KiB, MiB, GiB, ...
   *
   * @default metrication
   */
  displayUnitType: Capacity;
};

type ConvertBytesResponse = {
  /**
   * 연산된 용량을 의미합니다.
   */
  capacity: number;
  /**
   * displayUnitType과 convertLevel에 맞는 단위를 반환합니다.
   */
  unit: string;
};

const convertBytes = (request: ConvertBytesRequest): ConvertBytesResponse => {
  const {
    convertLevel = 1,
    convertType = Capacity.BINARY,
    displayUnitType = Capacity.METRICATION,
    decimalPoint = 2,
    bytes,
  } = request;

  if (bytes === 0) {
    return {
      capacity: 0,
      unit: CAPACITY_UNIT_MAP[displayUnitType][0],
    };
  }

  return {
    capacity: parseFloat(
      (bytes / Math.pow(CAPACITY_UNIT[convertType], convertLevel)).toFixed(decimalPoint),
    ),
    unit: CAPACITY_UNIT_MAP[displayUnitType][convertLevel],
  };
};

export { convertBytes, Capacity, CAPACITY_UNIT, CAPACITY_UNIT_MAP };

export type { ConvertBytesRequest, ConvertBytesResponse };
